import {
  addDays,
  addMonths,
  format,
  formatISO,
  getYear,
  startOfToday,
  startOfYear,
  subDays,
  subYears,
} from "date-fns";

import parseISO from "date-fns/parseISO";

const norwegianDateFormat = "dd.MM.yyyy";
const validInput = /^[0-9.]*$/;

export const validDDMMYYYYFormat = (value: string | undefined): boolean => {
  if (value) {
    return /^\d{2}\.\d{2}\.\d{4}$/g.test(value);
  }
  return false;
};

export const formatDateString = (value: Date): string | undefined => {
  try {
    return format(value, norwegianDateFormat);
  } catch (e) {
    try {
      return format(value, "yyyy-MM-dd");
    } catch (formatException) {
      return undefined;
    }
  }
};

export const humanReadableDateOfBirth = (
  dateOfBirth: string,
  hideDayFromDate = false,
): string => {
  return humanReadableDate(parseISO(dateOfBirth), undefined, hideDayFromDate);
};

export const getAge = (dateOfBirth: Date): number => {
  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

export const getBirthYear = (dateOfBirth: Date): number => {
  return dateOfBirth.getFullYear();
};

export const humanReadableDate = (
  date?: Date,
  locale?: string | string[],
  hideDayFromDate = false,
): string => {
  if (!date) {
    return "";
  }
  return date.toLocaleDateString(
    locale ? locale : ["nb-NO", "no"],
    hideDayFromDate
      ? hideDayFromLocaleDateStringConfig
      : localeDateStringConfig,
  );
};

export const hideDayFromLocaleDateStringConfig: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "long",
  day: undefined,
};

export const localeDateStringConfig: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "long",
  day: "numeric",
};

export const productTransferDate = (
  numberOfDays?: number,
  numberOfMonths?: number,
): Date => {
  if (numberOfDays !== undefined && numberOfMonths !== undefined) {
    return addMonths(addDays(new Date(), numberOfDays), numberOfMonths);
  }
  return addMonths(addDays(new Date(), 1), 1);
};

export const reverseDDMMYYYY = (date: string | undefined): string => {
  if (date) {
    let values: string[] = date.split(".");
    values = values.reverse();
    return values.join("-");
  }
  return "";
};

export const reverseAndPadDDMMYYYY = (date: string | undefined): string => {
  function toYYYY(year: string): string {
    if (year.length === 4) {
      return year;
    }
    const yy = parseInt(year, 10);
    return `${Math.trunc((getYear(new Date()) - yy) / 100)}${String(
      yy,
    ).padStart(2, "0")}`;
  }

  if (date) {
    let values: string[] = date.split(".");
    values = values.reverse();
    values[0] = toYYYY(values[0]);
    values[1] = String(values[1]).padStart(2, "0");
    values[2] = String(values[2]).padStart(2, "0");
    return values.join("-");
  }
  return "";
};

export const reverseYYYYMMDD = (date: string | undefined): string => {
  if (date) {
    let values: string[] = date.split("-");
    values = values.reverse();
    return values.join(".");
  }
  return "";
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export const formatDDMMYYYY = (currentDate: string, input: string): string => {
  if (input.match(validInput)) {
    if (currentDate) {
      if (
        input.length > currentDate.length &&
        (input.length === 2 || input.length === 5)
      ) {
        return `${input}.`;
      }
      if (currentDate.length === 2 && input.length === 3) {
        return `${input.slice(0, 2)}.${input.slice(2)}`;
      }
      if (currentDate.length === 5 && input.length === 6) {
        return `${input.slice(0, 5)}.${input.slice(5)}`;
      }
    }
    return input;
  }
  return currentDate;
};

export const getAgeFromYear = (year: number): number =>
  getYear(Date.now()) - year;

export const getYearFromAge = (age: number): Date =>
  subYears(startOfYear(Date.now()), age);

export const getYYYYMMDDFromAge = (age: number): string =>
  format(getYearFromAge(age), "yyyy-MM-dd");

export const formatDateToString = (date: Date): string =>
  formatISO(date, { representation: "date" });

export const getDateStringFromAge = (age: number): string => {
  const dateOfBirth = subYears(subDays(startOfToday(), 1), age);
  return dateOfBirth.toISOString().substring(0, 10);
};

// TODO-date-number-array - Remove this helper function when the backend is updated to return a string instead of an array of numbers
export const mapToDateString = (date: string | number[]): string => {
  if (typeof date === "string") {
    return date;
  }
  return date.map((value) => String(value).padStart(2, "0")).join("-");
};
